import { HelmetAsync } from '../components/HelmetAsync';
import { useReveal } from '../hooks/useReveal';
import { SectionLabel, Blob, Placeholder } from '../components/ui';
import { WHY_TILES } from '../data/content';
import { color, font, maxWidth } from '../styles/tokens';
import comp1 from '../assets/company/company1.png';
import cnc1 from '../assets/company/cnc1.png';

const HexTile = ({ tile }) => (
  <div data-reveal style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
    <div style={{ width: 120, height: 132, clipPath: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)', background: 'rgba(77,141,255,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 112, height: 122, clipPath: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)', background: '#0d1017', display: 'flex', alignItems: 'center', justifyContent: 'center', font: `700 clamp(18px,2vw,24px) ${font.display}`, color: color.accent, textAlign: 'center', padding: 10 }}>
        {tile.k}
      </div>
    </div>
    <div style={{ marginTop: 18, font: `600 15px ${font.display}`, color: color.text }}>{tile.t}</div>
    <div style={{ marginTop: 6, font: `400 13px/1.5 ${font.body}`, color: color.textDim, maxWidth: 180 }}>{tile.d}</div>
  </div>
);

export const AboutPage = () => {
  useReveal([]);

  return (
    <div style={{ animation: 'vt-fade .4s ease' }}>
      <HelmetAsync
        title="About — V-Tech Industries"
        description="Founded in 2012 in IMT Manesar, Gurugram, V-Tech Industries manufactures precision-engineered industrial tools for automotive, aerospace and hydraulics."
        keywords="V-Tech Industries, about, IMT Manesar, precision tooling manufacturer"
        url="/about"
      />

      {/* hero */}
      <section style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <Blob style={{ top: -160, right: -100, width: 520, height: 520, background: 'radial-gradient(circle,rgba(77,141,255,.4),transparent 64%)', opacity: 0.5 }} animation="vt-drift1 18s ease-in-out infinite" />
        <div style={{ position: 'relative', maxWidth, margin: '0 auto', padding: '88px 28px' }}>
          <SectionLabel data-reveal>[ ABOUT V-TECH ]</SectionLabel>
          <h1 data-reveal data-delay="80ms" style={{ margin: '18px 0 0', font: `700 clamp(38px,6vw,72px)/0.98 ${font.display}`, letterSpacing: '-.03em', color: color.text }}>
            Precision engineering for<br />
            <span style={{ color: color.accent }}>tomorrow's industries</span>
          </h1>
        </div>
      </section>

      {/* intro + mission/vision */}
      <section style={{ maxWidth, margin: '0 auto', padding: '80px 28px', display: 'flex', flexWrap: 'wrap', gap: 56 }}>
        <div data-reveal style={{ flex: '1 1 420px', minWidth: 300 }}>
          <p style={{ margin: 0, font: `400 19px/1.7 ${font.body}`, color: '#cdd4e0' }}>
            Founded in <strong style={{ color: color.accent }}>2012</strong>, V-Tech Industries — located in IMT Manesar, Sector 5,
            Gurugram — is a leading manufacturer of precision-engineered industrial tools.
          </p>
          <p style={{ margin: '20px 0 0', font: `400 16px/1.7 ${font.body}`, color: color.textDim }}>
            Over the years we have built a strong reputation for delivering high-quality, reliable and performance-driven solutions
            to a wide range of industries including automotive, aerospace and hydraulics.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 34 }}>
            <div style={{ background: 'rgba(77,141,255,.1)', border: '1px solid rgba(77,141,255,.25)', borderRadius: 18, padding: 26 }}>
              <div style={{ font: `500 12px ${font.mono}`, letterSpacing: '.12em', color: color.accentSoft }}>OUR MISSION</div>
              <ul style={{ margin: '16px 0 0', paddingLeft: 18, font: `400 14px/1.6 ${font.body}`, color: '#cdd4e0' }}>
                <li>Manufacture superior-quality tools with consistent performance</li>
                <li style={{ marginTop: 8 }}>Innovate continuously to meet evolving demands</li>
                <li style={{ marginTop: 8 }}>Build enduring customer relationships built on trust</li>
                <li style={{ marginTop: 8 }}>Contribute to India's industrial growth</li>
              </ul>
            </div>
            <div style={{ background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 18, padding: 26 }}>
              <div style={{ font: `500 12px ${font.mono}`, letterSpacing: '.12em', color: color.accent }}>OUR VISION</div>
              <p style={{ margin: '16px 0 0', font: `400 15px/1.7 ${font.body}`, color: '#cdd4e0' }}>
                To be a trusted partner and industry leader in advanced manufacturing solutions — driving progress through
                precision, innovation and reliability.
              </p>
            </div>
          </div>
        </div>

        <div data-reveal data-delay="120ms" style={{ flex: '1 1 320px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* <Placeholder label="facility · shop floor photo" style={{ aspectRatio: '4/3', borderRadius: 18 }} /> */}
          <img src={comp1} style={{ aspectRatio: '4/3', borderRadius: 18 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <img src={cnc1} height='260px' style={{ aspectRatio: '1', borderRadius: 18 }} />
            <Placeholder label="CNC line" style={{ aspectRatio: '1', borderRadius: 18 }} labelStyle={{ font: `500 9px ${font.mono}`, padding: '4px 7px' }} />
          </div>
        </div>
      </section>

      {/* why work with us */}
      <section style={{ background: 'rgba(255,255,255,.015)', borderTop: '1px solid rgba(255,255,255,.07)' }}>
        <div style={{ maxWidth, margin: '0 auto', padding: '80px 28px' }}>
          <h2 data-reveal style={{ margin: '0 0 44px', font: `600 clamp(28px,3.6vw,40px)/1.08 ${font.display}`, color: color.text, letterSpacing: '-.02em', textAlign: 'center' }}>
            Why work with V-Tech
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 26 }}>
            {WHY_TILES.map((w) => (
              <HexTile key={w.t} tile={w} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
